/**
 * Hollerpost plan catalog helpers: every price, monthly limit, pack price,
 * Free-plan term, founding offer and SMS line the product app shows comes
 * from here.
 *
 * The numbers live in ./hollerpostPlans.js, which is GENERATED by
 * scripts/export-hollerpost-plans.mjs from the hand-edited catalog
 * (supabase/functions/_shared/hollerpostCatalog.js) plus brands.js smsStatus.
 * hollerpost.com renders from the same export, and
 * scripts/check-hollerpost-copy.mjs fails CI if either side drifts. The same
 * module carries Utopian Creations' plans (UTOPIAN_PLANS) for the agency
 * dashboard.
 *
 * Plain data + functions, no import.meta, so Node build code can import it.
 */
import { HOLLERPOST_PLANS, UTOPIAN_PLANS } from './hollerpostPlans.js';

export const PLANS = HOLLERPOST_PLANS;

/** Hollerpost's paid plans by slug (Free is FREE, below). */
export const PLAN_BY_SLUG = Object.fromEntries(PLANS.plans.map((p) => [p.slug, p]));

/** Utopian Creations' plans by slug (agency dashboard). */
export const UTOPIAN_PLAN_BY_SLUG = Object.fromEntries(UTOPIAN_PLANS.plans.map((p) => [p.slug, p]));

const fmtN = (n) => Number(n).toLocaleString('en-US');

/** "$24" for whole dollars, "$24.50" otherwise. */
export const usd = (cents) => {
  const c = Math.round(Number(cents));
  return c % 100 === 0 ? `$${fmtN(c / 100)}` : `$${(c / 100).toFixed(2)}`;
};

/** Whole dollars as a number (PLAN_CATALOG keeps dollars). */
export const dollars = (cents) => Number(cents) / 100;

/** The Free plan: { slug, name, emails }. */
export const FREE = PLANS.free;
export const isFreePlan = (slug) => slug === FREE.slug;

/** "1,000 emails a month". */
export const freeTerms = () => `${fmtN(FREE.emails)} emails a month`;

/** Founding customers: { percent_off, customers }. */
export const FOUNDING = PLANS.founding;

/** Paid plans in a group: 'email' (email only) or 'texts' (email + texts). */
export const plansIn = (group) => PLANS.plans.filter((p) => p.group === group);

export const FREE_AI_USES = PLANS.free_ai_uses_per_month;
export const AI_ADDON_PRICE = usd(PLANS.ai_addon_cents); // "$10"

export const SMS_LIVE = PLANS.sms_status === 'live';

/** "1,000 texts a month" (live) / "1,000 texts a month once texting is live". */
export const smsQuotaLine = (smsQuota) =>
  SMS_LIVE ? `${fmtN(smsQuota)} texts a month` : `${fmtN(smsQuota)} texts a month once texting is live`;

// Any plan a tenant can be on, as { quotas, month_cents }: Hollerpost paid,
// Hollerpost Free, or Utopian.
const anyPlan = (slug) =>
  PLAN_BY_SLUG[slug]
  || (isFreePlan(slug) ? { slug, name: FREE.name, month_cents: 0, quotas: { email: FREE.emails, sms: 0 } } : null)
  || UTOPIAN_PLAN_BY_SLUG[slug]
  || null;

/** Product-mode plan summary: "10,000 emails a month · email only". */
export const planSummary = (slug) => {
  const p = anyPlan(slug);
  if (!p) return '';
  const email = `${fmtN(p.quotas.email)} emails a month`;
  return p.quotas.sms ? `${email} · ${smsQuotaLine(p.quotas.sms)}` : `${email} · email only`;
};

const packFor = (key) => PLANS.send_packs.find((x) => x.key === key) || UTOPIAN_PLANS.send_packs.find((x) => x.key === key);

/** Send pack button label: "+5,000 emails · $4" / "+250 texts · $5".
 *  `smsUnit` lets the agency dashboard keep saying "SMS credits". */
export const packLabel = (key, smsUnit = 'texts') => {
  const p = packFor(key);
  if (!p) return key;
  return `+${fmtN(p.amount)} ${p.channel === 'sms' ? smsUnit : 'emails'} · ${usd(p.cents)}`;
};

/** Pack keys for a channel: Hollerpost's (product) or Utopian's (agency). */
export const packKeys = (channel, product = true) =>
  (product ? PLANS : UTOPIAN_PLANS).send_packs.filter((p) => p.channel === channel).map((p) => p.key);

/** Upgrade nudge: "5x the sends for $8 more" (from Free: "for $8"). */
export const upgradeGain = (fromSlug, toSlug) => {
  const a = anyPlan(fromSlug);
  const b = anyPlan(toSlug);
  if (!a || !b || !a.quotas.email) return null;
  const x = b.quotas.email / a.quotas.email;
  return {
    multiple: Number.isInteger(x) ? `${x}x` : `${x.toFixed(1)}x`,
    more: usd(b.month_cents - a.month_cents),
    fromFree: isFreePlan(fromSlug),
  };
};
